import styles from "./ArticleContent.module.scss";
import { forwardRef, useCallback, useEffect, useState } from "react";
import {
  FragmentType,
  getFragmentData,
  graphql,
} from "../../../../__generated__/gql";
import {
  EditorContent,
  JSONContent,
  ReactRenderer,
  useEditor,
} from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { generateHTML } from "@tiptap/html";
import { Link } from "@tiptap/extension-link";
import { Placeholder } from "@tiptap/extension-placeholder";
import Focus from "@tiptap/extension-focus";
import { useToast } from "../../../../ui/Toast/ToastContext";
import ArticleContext from "./ArticleContext";
import CustomDocument from "./CustomDocument";
import CustomImage from "./CustomImage";
import Section from "./Section";
import SectionTitle from "./SectionTitle";
import CustomBubbleMenu from "./CustomBubbleMenu";
import TurndownService from "turndown";
import Commands from "./Commands";
import { Editor, Range } from "@tiptap/core";
import tippy, { Instance, Props } from "tippy.js";
import ChangeImageModal from "../ArticleImage/ChangeImageModal";
import { useDisclosure } from "../../../../ui/util/useDisclosure";
import CommandList from "./CommandList";
import {
  BulletList,
  Heading2,
  Heading3,
  Heading4,
  Image,
  MessageTextSquare,
  NumberList,
  Paragraph,
} from "../../../../ui/Icon/Icon";
import Title from "./Title";
import Excerpt from "./Excerpt";

const turndownService = new TurndownService();

const DOM_RECT_FALLBACK: DOMRect = {
  bottom: 0,
  height: 0,
  left: 0,
  right: 0,
  top: 0,
  width: 0,
  x: 0,
  y: 0,
  toJSON() {
    return {};
  },
};

const extensions = [
  CustomDocument,
  CustomImage.configure({
    inline: false,
  }),
  StarterKit.configure({
    document: false,
    heading: {
      levels: [2, 3, 4, 5, 6],
    },
  }),
  Underline,
  Link.configure({
    openOnClick: false,
  }),
  Title,
  Excerpt,
  Section,
  SectionTitle,
  Focus,
  Placeholder.configure({
    showOnlyCurrent: false,
    includeChildren: true,

    placeholder: ({ node }) => {
      if (node.type.name === "sectionTitle") {
        return "Section heading";
      }

      if (node.type.name === "title") {
        return "Article title";
      }

      if (node.type.name === "excerpt") {
        return "Article excerpt";
      }

      if (node.type.name === "paragraph") {
        return "Write something or '/' for commands";
      }

      return "Write something";
    },
  }),
];

const parseEditorContent = (content: JSONContent) => {
  const title = content.content
    ?.find((node) => node.type === "title")
    ?.content?.find((node) => node.type === "text")?.text;
  const excerpt = content.content
    ?.find((node) => node.type === "excerpt")
    ?.content?.find((node) => node.type === "text")?.text;
  const sections =
    content.content
      ?.filter((node) => node.type === "section")
      ?.map((section) => {
        const id: unknown = section.attrs?.id;
        const title = section.content
          ?.find((node) => node.type === "sectionTitle")
          ?.content?.find((node) => node.type === "text")?.text;

        const body = turndownService.turndown(
          generateHTML(
            {
              type: "doc",
              content: section.content?.filter((node) => {
                if (node.type === "sectionTitle") {
                  return false;
                }

                if (node.type === "image") {
                  return !!node.attrs?.src;
                }

                return !!node.content;
              }),
            },
            extensions,
          ),
        );

        return {
          id,
          title,
          body,
        };
      })
      .filter(
        (
          section,
        ): section is { id: string; title: string | undefined; body: string } =>
          !!(section.body && section.id),
      ) ?? [];

  return { title, excerpt, sections };
};

function EditableArticle({
  content,
  onUpdate,
}: {
  content: string;
  onUpdate: (data: {
    title?: string;
    excerpt?: string;
    sections: {
      id: string;
      title?: string;
      body: string;
    }[];
  }) => void | Promise<void>;
}) {
  const { showToast } = useToast();
  const [editorContent, setEditorContent] = useState<JSONContent>();
  const [changeImageModalOpened, changeImageModal] = useDisclosure();

  useEffect(() => {
    if (editorContent) {
      void onUpdate(parseEditorContent(editorContent));
    }
  }, [editorContent, onUpdate]);

  const editor = useEditor({
    onUpdate(e) {
      setEditorContent(e.editor.getJSON());
    },
    onTransaction(transaction) {
      if (transaction.transaction.getMeta("sectionDeletionPrevented")) {
        showToast({
          title: "Can't delete section",
          variant: "error",
        });
      }

      if (transaction.transaction.getMeta("imageDeletionPrevented")) {
        showToast({
          title: "Can't delete image",
          variant: "error",
        });
      }
    },
    extensions: [
      ...extensions,
      Commands.configure({
        suggestion: {
          items: ({ query }) => {
            return [
              {
                title: "Image",
                IconComponent: Image,
                command: ({
                  editor,
                  range,
                }: {
                  editor: Editor;
                  range: Range;
                }) => {
                  editor.chain().focus().deleteRange(range).run();
                  changeImageModal.open();
                },
              },
              {
                title: "Paragraph",
                IconComponent: Paragraph,
                command: ({
                  editor,
                  range,
                }: {
                  editor: Editor;
                  range: Range;
                }) =>
                  editor
                    .chain()
                    .focus()
                    .deleteRange(range)
                    .setParagraph()
                    .run(),
              },
              {
                title: "Heading 2",
                IconComponent: Heading2,
                command: ({
                  editor,
                  range,
                }: {
                  editor: Editor;
                  range: Range;
                }) =>
                  editor
                    .chain()
                    .focus()
                    .deleteRange(range)
                    .setHeading({ level: 2 })
                    .run(),
              },
              {
                title: "Heading 3",
                IconComponent: Heading3,
                command: ({
                  editor,
                  range,
                }: {
                  editor: Editor;
                  range: Range;
                }) =>
                  editor
                    .chain()
                    .focus()
                    .deleteRange(range)
                    .setHeading({ level: 3 })
                    .run(),
              },
              {
                title: "Heading 4",
                IconComponent: Heading4,
                command: ({
                  editor,
                  range,
                }: {
                  editor: Editor;
                  range: Range;
                }) =>
                  editor
                    .chain()
                    .focus()
                    .deleteRange(range)
                    .setHeading({ level: 4 })
                    .run(),
              },
              {
                title: "Bulleted list",
                IconComponent: BulletList,
                command: ({
                  editor,
                  range,
                }: {
                  editor: Editor;
                  range: Range;
                }) =>
                  editor
                    .chain()
                    .focus()
                    .deleteRange(range)
                    .toggleBulletList()
                    .run(),
              },
              {
                title: "Numbered list",
                IconComponent: NumberList,
                command: ({
                  editor,
                  range,
                }: {
                  editor: Editor;
                  range: Range;
                }) =>
                  editor
                    .chain()
                    .focus()
                    .deleteRange(range)
                    .toggleOrderedList()
                    .run(),
              },
              {
                title: "Quote",
                IconComponent: MessageTextSquare,
                command: ({
                  editor,
                  range,
                }: {
                  editor: Editor;
                  range: Range;
                }) =>
                  editor
                    .chain()
                    .focus()
                    .deleteRange(range)
                    .toggleBlockquote()
                    .run(),
              },
            ]
              .filter((item) => {
                const regex = new RegExp([...query].join(".*?"), "i");
                return regex.test(item.title);
              })
              .sort((a, b) => {
                if (!query) {
                  return 0;
                }

                const aIndex = a.title
                  .toLowerCase()
                  .indexOf(query.substring(0, 1).toLowerCase());
                const bIndex = b.title
                  .toLowerCase()
                  .indexOf(query.substring(0, 1).toLowerCase());

                if (aIndex !== -1 && (bIndex === -1 || aIndex < bIndex)) {
                  return -1;
                }

                if (bIndex !== -1 && (aIndex === -1 || bIndex < aIndex)) {
                  return 1;
                }

                return 0;
              })
              .slice(0, 10);
          },
          render: () => {
            let component: ReactRenderer;
            let popup: Instance<Props>[];

            return {
              onStart: (props) => {
                component = new ReactRenderer(CommandList, {
                  props,
                  editor: props.editor,
                });

                if (!props.clientRect) {
                  return;
                }

                popup = tippy("#article", {
                  getReferenceClientRect: () =>
                    props.clientRect?.() ?? DOM_RECT_FALLBACK,
                  appendTo: () =>
                    document.querySelector("#article") ?? document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: "manual",
                  placement: "bottom-start",
                });
              },
              onUpdate(props) {
                component.updateProps(props);

                popup[0].setProps({
                  getReferenceClientRect: () =>
                    props.clientRect?.() ?? DOM_RECT_FALLBACK,
                });
              },
              onKeyDown(props) {
                if (props.event.key === "Escape") {
                  popup[0].hide();

                  return true;
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                return (component.ref as any)?.onKeyDown(props);
              },
              onExit() {
                popup[0].destroy();
                component.destroy();
              },
            };
          },
        },
      }),
    ],
    content,
  });

  const onSubmit = useCallback(
    ({ id, alt }: { id: string; alt: string }) => {
      if (!editor) {
        return;
      }

      editor
        .chain()
        .focus()
        .insertContent({
          type: "image",
          attrs: {
            src: `${import.meta.env.VITE_IMAGE_HOST}/${id}#${id}`,
            alt,
          },
        })
        .run();
    },
    [editor],
  );

  return (
    <div>
      <EditorContent
        editor={editor}
        className={styles.editor}
        onClick={() => {
          if (!editor) {
            return;
          }

          const sections = editor.$doc.querySelectorAll("section");
          const lastSection = sections[sections.length - 1];
          const selectedNode = editor.state.selection.$head.node(
            lastSection.depth,
          );

          if (
            selectedNode?.eq(lastSection.node) &&
            editor.state.selection.head === editor.state.selection.$head.end()
          ) {
            editor?.commands.insertContent({
              type: "paragraph",
            });
          }
        }}
      />
      {editor && <CustomBubbleMenu editor={editor} />}

      <ChangeImageModal
        isOpen={changeImageModalOpened}
        onClose={changeImageModal.close}
        onSubmit={onSubmit}
      />
    </div>
  );
}

const ArticleSectionFragment = graphql(/* GraphQL */ `
  fragment ArticleSection on ArticleSection {
    id
    body
    bodyHtml
    title
    titleDisabled
    status
    additionalPrompt
  }
`);

const ArticleContent = forwardRef<
  HTMLDivElement,
  {
    onUpdate: (data: {
      title?: string;
      excerpt?: string;
      sections: {
        id: string;
        title?: string;
        body: string;
      }[];
    }) => void | Promise<void>;
    articleId: string;
    title: string;
    excerpt: string;
    sections: FragmentType<typeof ArticleSectionFragment>[];
  }
>(function ArticleContent(
  { articleId, sections, onUpdate, title, excerpt },
  ref,
) {
  return (
    <div className={styles.articleContent} ref={ref}>
      <ArticleContext.Provider
        value={{
          sections: sections.map((section) => {
            const {
              id,
              additionalPrompt,
              status,
              title,
              titleDisabled,
              bodyHtml,
            } = getFragmentData(ArticleSectionFragment, section);

            return {
              id: id,
              additionalPrompt: additionalPrompt ?? "",
              articleId,
              body: bodyHtml ?? "",
              title,
              titleDisabled,
              status: status,
            };
          }),
        }}
      >
        <EditableArticle
          onUpdate={onUpdate}
          content={`<title>${title}</title><excerpt>${excerpt}</excerpt>${sections
            .map((section) => {
              const { id, title, bodyHtml } = getFragmentData(
                ArticleSectionFragment,
                section,
              );

              return `<section data-id="${id}"><h2>${title}</h2>${
                bodyHtml ?? ""
              }</section>`;
            })
            .join("")}`}
        />
      </ArticleContext.Provider>
    </div>
  );
});

export default ArticleContent;
